import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDispatch, useSelector } from "react-redux";
import { getActiveCountriesRequest } from "../../redux/Dashboard/dashboardSlice";
import { useAppTheme } from "../../theme/useAppTheme";
import { pageBackground, primaryButtonSx } from "../../theme/resolveThemeColors";

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeCountries = [] } = useSelector(
    (state) => state.dashboard || {}
  );
  const themeColors = useAppTheme();

  const storedCountryID = localStorage.getItem("countryID");
  const [statesByCountry, setStatesByCountry] = useState([]);
  useEffect(() => {
    dispatch(
      getActiveCountriesRequest({
        jeweller: process.env.REACT_APP_JEWELLER_ID,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (activeCountries.length && storedCountryID) {
      const matchedCountry = activeCountries.find(
        (c) => c._id === storedCountryID
      );

      if (matchedCountry?.states?.length) {
        setStatesByCountry(matchedCountry.states);
      }
    }
  }, [activeCountries, storedCountryID]);

  const [loadingState, setLoadingState] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    birthDate: null,
    phone: "",
    gender: "",
    pincode: "",
    state: "",
    joiningCode: "",
    city: "",
    area: "",
    address1: "",
    address2: "",
    address3: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const noLeadingSpaceFields = [
      "firstName",
      "lastName",
      "email",
      "pincode",
      "joiningCode",
    ];
    const nextValue = noLeadingSpaceFields.includes(name)
      ? value.replace(/^\s+/, "")
      : value;

    if (name === "pincode") {
      setFormData((prev) => ({ ...prev, [name]: nextValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: nextValue }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, birthDate: date }));
  };

  const validate = () => {
    const {
      firstName,
      lastName,
      email,
      birthDate,
      gender,
      phone,
      pincode,
      state,
    } = formData;

    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      toast.error("First and last name must be at least 2 characters.");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      toast.error("Enter a valid email address.");
      return false;
    }

    if (!pincode) {
      toast.error("Pincode is required.");
      return false;
    }

    if (!/^\d+$/.test(pincode)) {
      toast.error("Pincode must contain only numbers.");
      return false;
    }

    if (!birthDate) {
      toast.error("Please select your birth date.");
      return false;
    }

    if (!gender) {
      toast.error("Please select your gender.");
      return false;
    }

    if (!state) {
      toast.error("Please select your state.");
      return false;
    }

    return true;
  };

  const storedPhone = localStorage.getItem("otpPhone");
  const storedcountryID = localStorage.getItem("countryID");
  console.log("Stored phone number:", storedPhone);
  const storedRefreshToken = localStorage.getItem("refreshToken");
  const storedAccessToken = localStorage.getItem("accessToken");

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/customerCreate`,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: storedPhone,
          dateOfBirth: dayjs(formData.birthDate).valueOf(),
          gender: formData.gender,
          jeweller: process.env.REACT_APP_JEWELLER_ID,
          joining_code: formData.joiningCode,
          address: [
            {
              pincode: formData.pincode,
              state: formData.state,
              city: formData.city || "",
              area: formData.area || "",
              address1: formData.address1 || "",
              address2: formData.address2 || "",
              address3: formData.address3 || "",
              addressType: "",
            },
          ],
          deviceDetails: Navigator.userAgent,
          refreshToken: storedRefreshToken || "",
          accessToken: storedAccessToken || "",
          customerType: "Standard",
          country: storedcountryID,
        }
      );

      if (response?.data?.success) {
        toast.success("Registered successfully!");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          birthDate: null,
          phone: "",
          gender: "",
          pincode: "",
          state: "",
          joiningCode: "",
          city: "",
          area: "",
          address1: "",
          address2: "",
          address3: "",
        });
        navigate("/login");
      } else {
        toast.error(response?.data?.message || "Something went wrong.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Registration failed.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: pageBackground(themeColors),
        color: themeColors.scaffoldText,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: "100%",
          borderRadius: 3,
          boxShadow: 8,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ mb: 1, fontSize: { xs: "1.8rem", sm: "2.2rem" } }}
          >
            Register New User
          </Typography>

          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{ width: "100%", mb: 2 }}
              label="Date of Birth"
              value={formData.birthDate}
              onChange={handleDateChange}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </LocalizationProvider>

          <TextField
            select
            fullWidth
            label="Gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Pin Code"
            name="pincode"
            value={formData.pincode}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 2 }}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />

          <TextField
            select
            fullWidth
            label={loadingState ? "Fetching State..." : "State"}
            name="state"
            value={formData.state}
            onChange={handleChange}
            disabled={loadingState}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            {statesByCountry.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Refferal Code"
            name="joiningCode"
            value={formData.joiningCode}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 3 }}
          />

          <Button
            fullWidth
            variant="contained"
            sx={primaryButtonSx(themeColors, {
              fontSize: { xs: "1rem", sm: "24px" },
              py: 1.5,
            })}
            onClick={handleSubmit}
          >
            Register
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Register;
